import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { debounce } from '@mui/material/utils';
import { z } from 'zod';
import type { ColDef, GridState, StateUpdatedEvent } from 'ag-grid-community';

import { storage, parseJSON } from 'lib/storage';
import { GRID_SERVICE_KEYS, reconcileAgGridStateWithColumnDefs } from 'components/AgGrid/helpers';
import useAgGridRtlMethods from 'components/AgGrid/hooks/useAgGridRtlMethods';
import { userIdSelector } from 'domain/env/envSelector';
import type { BankFeedAccountType } from 'domain/bankFeed/schemas';

// Persisted slice — explicit allowlist (NOT the whole GridState). filter/sort/pagination
// deliberately excluded: they drive the SSRM server query / cursor paging and must not be
// restored. `version` is AG Grid's own field — kept so the grid's built-in state migration
// (migrateGridStateModel) runs correctly after an AG Grid upgrade.
// Nested arrays are `.optional()`, not required: be tolerant of a sub-object that omits an array
// (forward-compat with AG Grid's emitted shape) so a valid slot is never dropped as "corrupt".
// Wrong-typed values (e.g. a string where an array is expected) still fail parse → default
// fallback; downstream already null-guards the arrays (`?.orderedColIds || []`, etc.).
const persistedGridStateSchema = z.object({
  version: z.string().optional(),
  columnOrder: z.object({ orderedColIds: z.array(z.string()).optional() }).optional(),
  columnSizing: z
    .object({
      columnSizingModel: z
        .array(z.object({ colId: z.string(), width: z.number().optional(), flex: z.number().optional() }))
        .optional(),
    })
    .optional(),
  columnVisibility: z.object({ hiddenColIds: z.array(z.string()).optional() }).optional(),
  columnPinning: z
    .object({ leftColIds: z.array(z.string()).optional(), rightColIds: z.array(z.string()).optional() })
    .optional(),
});
type PersistedGridState = z.infer<typeof persistedGridStateSchema>;

// Amount-bearing columns pinned right by default, per account type: the Bank tab splits the
// figure into credit/debit, the Credit Card tab delivers a single signed `amount`. They pin
// right so they stay visible without horizontal scroll on a 13" screen (#2); a user re-pin/unpin
// persists over it. Keyed per tab because a default pinning debit/credit on the CC tab (whose
// colDefs lack those fields) would leave the CC `amount` column unpinned.
const DEFAULT_RIGHT_PINNED_AMOUNT_COLS: Record<BankFeedAccountType, string[]> = {
  CHECKING: ['debit', 'credit'],
  CARD: ['amount'],
};

// Default layout for a fresh user/tab (no stored config). Authored in LTR ("canonical")
// orientation — the hook flips the pinned side for RTL via invertPinnedStateWhenRTL, the same
// transform used when reading/writing the persisted state. The columns-visibility service column
// MUST be listed too: AG Grid applies columnPinning wholesale (ignores partialColumnState here),
// so any right-pinned column omitted from rightColIds gets unpinned. It stays last via its colDef
// lockPosition:'right'.
const buildDefaultGridState = (tabKey: BankFeedAccountType): GridState => ({
  columnPinning: {
    leftColIds: [],
    rightColIds: [...DEFAULT_RIGHT_PINNED_AMOUNT_COLS[tabKey], GRID_SERVICE_KEYS.COLUMNS_VISIBILITY_MENU],
  },
});

// Whole store: { `${userID}-${tabKey}`: PersistedGridState }. Per-slot validation (below)
// keeps one corrupt/foreign slot from nuking the others.
const config = () => storage().bankFeedTransactionsAgGridConfig();
const readStore = (): Record<string, unknown> => {
  // No `?? ''` fallback: `JSON.parse('')` throws (and parseJSON logs it) for the common
  // no-config-yet case, whereas a null key passes through parseJSON → null silently.
  const parsed = parseJSON(config().get());
  // Reject arrays: `typeof [] === 'object'`, and spreading an array into the store on write
  // would leak numeric keys. The store must stay a plain `{ [token]: slot }` map.
  return parsed != null && typeof parsed === 'object' && !Array.isArray(parsed)
    ? (parsed as Record<string, unknown>)
    : {};
};

const pickPersisted = (state: GridState): PersistedGridState => ({
  version: state.version,
  columnOrder: state.columnOrder,
  columnSizing: state.columnSizing,
  columnVisibility: state.columnVisibility,
  columnPinning: state.columnPinning,
});

type Params = { tabKey: BankFeedAccountType; columnDefs: ColDef[] };

export const useBankFeedTransactionsGridState = ({ tabKey, columnDefs }: Params) => {
  const userID = useSelector(userIdSelector);
  const { invertPinnedStateWhenRTL } = useAgGridRtlMethods();
  const token = `${userID}-${tabKey}`;

  // Skip redundant writes: onStateUpdated fires for scroll/focus too, where our slice is unchanged.
  const lastWrittenRef = useRef<string>('');

  const initialState = useMemo<GridState>(() => {
    if (columnDefs.length === 0) return {};

    // No userID yet (pre-auth-hydration render) → skip storage and show the default layout;
    // reading/writing under an `undefined-<tab>` token could touch the wrong user's slot.
    const result = userID ? persistedGridStateSchema.safeParse(readStore()[token]) : null;
    // DEFAULT is the baseline; a valid stored config overrides it per-key (so a config saved
    // before columnPinning existed still gets the default pin). No / corrupt config → just DEFAULT.
    const defaultState = buildDefaultGridState(tabKey);
    const base = result?.success
      ? {
          ...defaultState,
          ...result.data,
          ...reconcileAgGridStateWithColumnDefs(result.data, columnDefs),
        }
      : defaultState;

    return invertPinnedStateWhenRTL({ ...base, partialColumnState: true });
  }, [userID, token, tabKey, columnDefs, invertPinnedStateWhenRTL]);

  const persist = useCallback(
    (state: GridState) => {
      // Don't persist before userID hydrates — would write under an `undefined-<tab>` token.
      if (!userID) return;
      // Store in canonical (LTR) orientation so a locale switch (LTR<->RTL) doesn't flip pins.
      const slice = pickPersisted(invertPinnedStateWhenRTL(state));
      // Key the dedup guard by token too, so a token change (e.g. userID resolving) never
      // suppresses the first write under the new token.
      const serialized = `${token}|${JSON.stringify(slice)}`;
      if (serialized === lastWrittenRef.current) return;
      lastWrittenRef.current = serialized;

      config().set(JSON.stringify({ ...readStore(), [token]: slice }));
    },
    [userID, token, invertPinnedStateWhenRTL],
  );

  const onStateUpdated = useMemo(() => debounce((event: StateUpdatedEvent) => persist(event.state), 250), [persist]);

  useEffect(() => () => onStateUpdated.clear(), [onStateUpdated]);

  return { initialState, onStateUpdated };
};
